import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import BankTransferConfirmation from '../BankTransferConfirmation';
import { Button, TableWrapper } from 'components/Global';

import { ReactComponent as CarCharterIcon } from 'icons/car-charter-filled-icon.svg';
import { ReactComponent as LeftArrow } from 'icons/left-arrow.svg';
import PriceDifferenceModal from 'components/Modals/PriceDifferenceModal';
import UpdateDataForm from './update-data-form';
import RegularDataForm from './regular-data-form';
import { useDispatch, useSelector } from 'react-redux';
import { getCustomerData } from 'features/user/slice';
import countryCodes from 'utils/country-codes.json';
import { useAppContext } from 'components/Context/AppContext';
import { updateApprovalStatus } from 'features/approvals/actions';
import { trackEvent } from 'utils/analytics';
import { changeOrderStatus, changePaymentType } from 'features/orders/actions';
import { getDetailDriver } from 'features/drivers/actions';
import { checkEmptyObject, getDurationExtend, getStatusOrderState } from 'utils/functionality';
import { calculatingFeeTask } from 'features/calculate-fee-task/actions';
import { FeatureFlag } from 'utils/feature-flag';
import { useFliptBoolean } from '@flipt-io/flipt-client-react';

const getExtendCharterData = (bookingZones) => {
  let extendDataCharter = {};

  for (const childBooking of bookingZones) {
    if (Object.prototype.hasOwnProperty.call(childBooking, 'extension') && childBooking?.extension?.length !== 0) {
      if (childBooking.extension[0]?.order_parent_status === 'CHECKOUT') {
        extendDataCharter = childBooking.extension[0];
      }
    }
  }

  return extendDataCharter;
};

const DetailOrder = ({ orderData: data, status, imagePreviewHandler, getFileName, transactionKey }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user = useSelector((state) => state.user.profile);
  const isPublishTaskCharterFlowEnabled = useFliptBoolean(
    'FLAG_FE_PUBLISH_TASK_CHARTER_FLOW',
    false,
    user.id || 'anonymous',
  );

  const { setShowConfirmation, showToast, setShowSpinner, setShowBroadcastTaskCharter, setShowCancelExtendCharter } =
    useAppContext();

  const customer = useSelector(getCustomerData);
  const findCountryByCode = (code) => countryCodes.find((item) => item.dial_code == code);
  const { selected: selectedDriver } = useSelector((state) => state.drivers);

  const [showBankTransferConfirmation, setShowBankTransferConfirmation] = useState(false);
  const [isPaymentValid, setIsPaymentValid] = useState(false);
  const [showPriceDifference, setShowPriceDifference] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState({
    name: '',
    code: '',
  });

  const isOrderCancelled = data?.order_status === 'CANCELLED';
  const isOrderFinished = data?.order_status === 'FINISHED';
  const isNotApproved = !data?.order_approval_status;
  const hasNoDriverTasks =
    data?.order_driver_tasks !== null &&
    Array.isArray(data?.order_driver_tasks) &&
    data?.order_driver_tasks.length === 0;
  const isTaskCancelled = data?.order_driver_tasks?.[0]?.status === 'CANCEL';
  const isPaidOrder = data?.order_status === 'PAID';
  const showCancelOrderBtn = !isOrderCancelled && !isOrderFinished && !data.order_approval_status;
  const showRejectOrderBtn =
    (data.order_status === 'CHECKOUT' || data.order_status === 'REVIEWING_IDENTITY') && !data.order_approval_status;

  const showPublishButton =
    (isPaidOrder && isNotApproved && hasNoDriverTasks) || (isTaskCancelled && !isOrderCancelled);

  const extendCharterData =
    data && data?.order_detail?.booking_zones && data?.order_detail?.booking_zones?.length !== 0
      ? getExtendCharterData(data?.order_detail?.booking_zones)
      : {};

  const checkIsDisable = (tasks) => {
    if (!tasks) return;

    if (tasks.length > 1) {
      const nonCancelItems = tasks.filter((item) => item.string !== 'CANCEL');

      if (nonCancelItems.length >= 2) {
        const sameTypes = new Set(nonCancelItems.map((item) => item.task_type));
        return sameTypes.size > 1;
      } else {
        return false;
      }
    } else {
      return false;
    }
  };

  useEffect(() => {
    if (customer) {
      setSelectedCountry(findCountryByCode(data?.is_admin_creation ? data?.phone_country_code : customer?.phone_code));
    }
  }, [customer, data]);

  useEffect(() => {
    if (
      !data ||
      checkEmptyObject(data) ||
      !data.order_driver_tasks ||
      data.order_driver_tasks.length === 0 ||
      !data.order_driver_tasks[0].driver_id
    )
      return;

    dispatch(getDetailDriver(data.order_driver_tasks[0].driver_id));
  }, [data]);

  useEffect(() => {
    if (status === 'loading') {
      setShowSpinner(true);
      return;
    }
    setShowSpinner(false);
  }, [status]);

  const backHandler = () => {
    navigate(-1);
  };

  const handleProcess = () => {
    const { prevStatus, nextStatus, confirmationMessage, toastErrorMessage, toastSuccessMessage } = getStatusOrderState(
      data?.order_status,
    );

    const payload = {
      prevStatus,
      nextStatus,
      transaction_key: data?.transaction_key,
    };

    setShowConfirmation({
      message: confirmationMessage,
      show: true,
      onClick: async () => {
        try {
          trackEvent('order_status_advanced', {
            order_id: data?.transaction_key,
            to_status: nextStatus,
            source: 'detail_order_car_charter',
            via_approval: Boolean(data?.order_approval_status?.includes('UPDATE_ORDER')),
          });
          if (data?.order_approval_status?.includes('UPDATE_ORDER')) {
            await dispatch(updateApprovalStatus({ status: 'approved', id: data?.approval?.id, skip: true })).unwrap();
          } else {
            await dispatch(changeOrderStatus(payload)).unwrap();
          }
          showToast({ type: 'success', message: toastSuccessMessage });

          if (data.type === 'HALF' && data.order_status === 'PAID') {
            await dispatch(changePaymentType(data?.transaction_key)).unwrap();
          }

          setShowBankTransferConfirmation(false);
        } catch (err) {
          showToast({ type: 'error', message: toastErrorMessage });
        }
      },
    });
  };

  const showBroadcastHandler = () => {
    setShowBroadcastTaskCharter(true);
    dispatch(calculatingFeeTask(data?.transaction_key));
  };

  const confirmExtendCharterHandler = () => {
    const { prevStatus, nextStatus, toastErrorMessage, toastSuccessMessage } = getStatusOrderState(data?.order_status);

    const payload = {
      prevStatus,
      nextStatus,
      transaction_key: extendCharterData?.order_transaction_key,
    };

    setShowConfirmation({
      show: true,
      message: 'Apakah anda ingin Extend Order Pesanan ini?',
      onClick: async () => {
        try {
          trackEvent('order_status_advanced', {
            order_id: extendCharterData?.order_transaction_key,
            to_status: nextStatus,
            source: 'detail_order_car_charter',
            via_approval: false,
          });
          await dispatch(changeOrderStatus(payload)).unwrap();

          showToast({ type: 'success', message: toastSuccessMessage });
        } catch (err) {
          showToast({ type: 'error', message: toastErrorMessage });
        }
      },
      variant: 'primary',
      valueButton: 'Lanjutkan',
    });
  };

  const rejectExtendCharterHandler = () => {
    setShowConfirmation({
      show: true,
      message: 'Apakah anda ingin Menolak Extend Order pesanan ini?',
      onClick: () =>
        setShowCancelExtendCharter({ show: true, transaction_key: extendCharterData?.order_transaction_key }),
      variant: 'primary',
      valueButton: 'Lanjutkan',
    });
  };

  return (
    <>
      {showBankTransferConfirmation ? (
        <BankTransferConfirmation
          imagePreviewHandler={imagePreviewHandler}
          orderData={data}
          setShowBankTransferConfirmation={setShowBankTransferConfirmation}
          setIsPaymentValid={setIsPaymentValid}
          isPaymentValid={isPaymentValid}
          handleProcess={handleProcess}
        />
      ) : (
        <div className="detail-car-charter">
          <div className="detail-car-charter__back-btn" onClick={backHandler}>
            <LeftArrow />
            <p>Kembali</p>
          </div>

          {isPublishTaskCharterFlowEnabled && showPublishButton && (
            <Button
              className="detail-car-charter__publish-btn"
              style={{
                backgroundColor: checkIsDisable(data?.order_driver_tasks) ? '#e0e0e0' : '#007A05',
              }}
              onClick={showBroadcastHandler}
              disabled={checkIsDisable(data?.order_driver_tasks)}
            >
              Publish Task to Driver
            </Button>
          )}

          <FeatureFlag flagKey={'FLAG_EXTEND_CHARTER'} entityId={'anonymus'}>
            {extendCharterData && !checkEmptyObject(extendCharterData) && (
              <div className="detail-car-charter__extend-order-request">
                <div className="extend-form">
                  <h1>Permintaan Extend Pesanan Charter</h1>
                  <p>{data?.user_name} ingin mengajukan perpanjangan masa sewa untuk mobil dengan nomor pemesanan :</p>
                  <p>ID Nomor Pesanan : {extendCharterData?.id}</p>
                  <p>
                    Dengan tipe sewa : {extendCharterData?.charter_duration_label} (
                    {extendCharterData?.charter_duration} Jam)
                  </p>
                  <p>
                    Durasi Extend :{' '}
                    {getDurationExtend(
                      extendCharterData?.booking_start_time,
                      extendCharterData?.charter_duration,
                      extendCharterData?.booking_end_time,
                    )}{' '}
                    Jam
                  </p>
                  <p>
                    Mohon cek ketersediaan mobil untuk menindak lanjuti permintaan extend car charter, Terima kasih.
                  </p>
                </div>

                <div className="extend-btn">
                  <Button type="button" variant="primary" width={78} height={27} onClick={rejectExtendCharterHandler}>
                    Tolak
                  </Button>
                  <Button type="button" variant="primary" width={78} height={27} onClick={confirmExtendCharterHandler}>
                    Terima
                  </Button>
                </div>
              </div>
            )}
          </FeatureFlag>

          <TableWrapper
            icon={<CarCharterIcon fill="#009EF7" width="25px" height="25px" />}
            title="Car Charter"
            CustomFilterComponent={
              Object.prototype.hasOwnProperty.call(data, 'order_approval_history') && (
                <div className="btn-export">
                  <Button variant="primary" onClick={() => setShowPriceDifference(true)}>
                    Detail Perbedaan
                  </Button>
                </div>
              )
            }
          >
            {data?.order_approval_status?.includes('UPDATE_ORDER') ? (
              <UpdateDataForm
                customer={customer}
                data={data}
                getFileName={getFileName}
                imagePreviewHandler={imagePreviewHandler}
                selectedCountry={selectedCountry}
              />
            ) : (
              <RegularDataForm
                customer={customer}
                data={data}
                getFileName={getFileName}
                imagePreviewHandler={imagePreviewHandler}
                selectedCountry={selectedCountry}
                showCancelOrderBtn={showCancelOrderBtn}
                showRejectOrderBtn={showRejectOrderBtn}
                transactionKey={transactionKey}
                showProcessBtn
                isPaymentValid={isPaymentValid}
                handleProcess={handleProcess}
                setShowBankTransferConfirmation={setShowBankTransferConfirmation}
                driverData={selectedDriver}
              />
            )}
          </TableWrapper>

          {showPriceDifference &&
            ReactDOM.createPortal(
              <PriceDifferenceModal
                setShowPriceDifference={setShowPriceDifference}
                Icon={CarCharterIcon}
                title="Lepas Kunci"
                data={data}
              />,
              document.getElementById('modal'),
            )}
        </div>
      )}
    </>
  );
};

export default DetailOrder;
